import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { LineReveal, EASE } from "./Motion";
import { scrollToTarget } from "../lib/smooth";
import { useLightbox } from "./Lightbox";

const Meta = ({ k, v, d }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: d }}>
    <p className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-ink">{k}</p>
    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted">{v}</p>
  </motion.div>
);

const Stack = ({ delay }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 90, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 90, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  const { open } = useLightbox();
  const cards = [
    { src: "/work/p00_1769.webp", cls: "right-[0%] top-[0%] w-[82%] aspect-[16/10]", z: 0 },
    { src: "/work/p15_113.webp", cls: "left-[0%] top-[30%] w-[36%] aspect-[3/4]", z: 50 },
    { src: "/work/p09_201.webp", cls: "right-[6%] top-[58%] w-[44%] aspect-[16/10]", z: 90 },
  ];
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative mb-6 h-[78vw] sm:h-[70vw] md:mb-0 md:h-[38vw] [perspective:1400px]" data-testid="hero-stack">
      <motion.div style={{ rotateX: rx, rotateY: ry, y, transformStyle: "preserve-3d" }} className="absolute inset-0">
        {cards.map((c, i) => (
          <motion.div
            key={c.src}
            className={`absolute cursor-zoom-in overflow-hidden shadow-[0_30px_60px_-30px_rgba(20,20,20,0.55)] ${c.cls}`}
            onClick={() => open(cards.map((x) => x.src), i)}
            data-testid={`hero-stack-image-${i}`}
            style={{ transform: `translateZ(${c.z}px)` }}
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, ease: EASE, delay: delay + 0.35 + i * 0.12 }}
          >
            <motion.img src={c.src} alt="" className="h-full w-full object-cover" initial={{ scale: 1.35 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: EASE, delay: delay + 0.35 + i * 0.12 }} />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export const Hero = ({ delay = 0 }) => (
  <section className="relative px-5 pb-16 pt-28 md:px-10 md:pb-24 md:pt-32" data-testid="hero-section">
    <div className="grid grid-cols-3 gap-4 border-b border-line pb-5">
      <Meta k="Portfolio" v="Ravin Patel" d={delay + 0.1} />
      <div className="justify-self-center text-center"><Meta k="Discipline" v="Multiple" d={delay + 0.2} /></div>
      <div className="justify-self-end text-right"><Meta k="Year" v="2009 — Present" d={delay + 0.3} /></div>
    </div>
    <div className="mt-10 grid items-end gap-10 lg:mt-14 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <LineReveal
          testid="hero-headline"
          delay={delay}
          className="font-display text-[13vw] font-semibold leading-[0.9] tracking-[-0.055em] text-ink sm:text-[11vw] lg:text-[7.4vw]"
          lines={[
            "Crafting",
            <>purposeful <em className="font-serif font-normal italic tracking-[-0.02em] text-ink">brands,</em></>,
            "interfaces",
            <>&amp; spaces.</>,
          ]}
        />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: delay + 0.9 }} className="mt-10 flex flex-wrap items-end justify-between gap-6">
          <p className="max-w-sm text-base leading-relaxed text-muted">
            <span className="text-ink">Ravin Patel</span> — Creative Lead working across UX, brand identity, print, retail POS and CGI for home, interiors and retail brands.
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => scrollToTarget("#work")} className="pill-btn" data-testid="hero-view-work-btn">
              <span>View work</span><span aria-hidden>↓</span>
            </button>
            <a href="/Ravin_Patel_CV.pdf" download className="ghost-btn" data-testid="hero-download-cv-btn">
              <span>Download CV</span><span aria-hidden>↧</span>
            </a>
          </div>
        </motion.div>
      </div>
      <div className="lg:col-span-5">
        <Stack delay={delay} />
      </div>
    </div>
  </section>
);
